import React, { useEffect, useState } from 'react';

import { SAMPLE_REQUEST_TYPES } from '@/constants/constant';

import ModalContainer from '@/components/modals/ModalContainer';
import CommonImage from '@/components/CommonImage';
import ModalHeader from '@/components/modals/ModalHeader';
import InputField from '@/components/InputField';
import ModalFooter from '@/features/sampleDevelopment/components/SampleRequestModals/ModalFooter';
import { useProductDevelopmentAddMutation } from '@/features/Scan/api/scanApi';
import LoaderComponent from '@/components/LoaderComponent';
import { toastError, toastSuccess } from '@/components/Toast';
import { useGetProductInfoByIdsQuery } from '@/features/sampleDevelopment/api/sampleDevelopmentApi';
import { packTag, setTag } from '@/utils/Util';
import { useGetEnumsQuery, useGetUserByTypeQuery } from '@/api/commonApis';
import CustomDropdown from '@/components/CustomDropdown';
import { SAMPLE_REQUEST_ENUM } from '@/features/sampleDevelopment/constants';
import { formatSampleTypeOptions } from '@/features/sampleDevelopment/utils';

const RequestTo3DAndGraphicsModal = ({
	requestType,
	onClose,
	title,
	productIds,
	onRefetch = () => {},
	onResetError = () => {}
}) => {
	const [requiredDate, setRequiredDate] = useState('');
	const [selectedUser, setSelectedUser] = useState(null);
	const [selectedSampleType, setSelectedSampleType] = useState(null);
	const [productDevelopmentAdd, { isLoading: isSubmitting }] = useProductDevelopmentAddMutation();
	const { data: productInfo = [], isLoading: productLoading } = useGetProductInfoByIdsQuery({
		params: { ids: productIds.join(',') }
	});
	const { data: userList = { FASHION_DESIGNER: [], GRAPHICS_DESIGNER: [], THREE_D_DESIGNER: [] } } =
		useGetUserByTypeQuery({
			params: {
				userTypes: 'FASHION_DESIGNER,GRAPHICS_DESIGNER,THREE_D_DESIGNER'
			}
		});
	const [errors, setErrors] = useState(null);

	const { data: sampleTypeList } = useGetEnumsQuery({
		path: SAMPLE_REQUEST_ENUM.SAMPLE_DEVELOPMENT_TYPE_ENUM
	});

	useEffect(() => {
		onResetError([]);
	}, []);

	const handleSuccess = () => {
		onRefetch();
		toastSuccess('Updated successfully');
		setRequiredDate('');
		onClose();
	};

	const isValidInputFields = () => {
		const newError = {};
		if (!requiredDate) {
			newError.requiredDate = 'Required date is mendatory';
		}
		if (requestType === SAMPLE_REQUEST_TYPES.SEND_TO_GRAPHIC) {
			if (!selectedUser) {
				newError.selectedUser = 'Graphics designer is mendatory';
			}
		}
		if (requestType === SAMPLE_REQUEST_TYPES.SEND_TO_3D) {
			if (!selectedSampleType) {
				newError.selectedSampleType = 'Sample type is mendatory';
			}
		}

		setErrors(newError);
		return Object.keys(newError)?.length === 0;
	};

	const handleSubmit = () => {
		if (!isValidInputFields()) {
			return;
		}

		if (requestType === SAMPLE_REQUEST_TYPES.SEND_TO_3D) {
			const body = {
				productActionId: 388, // Will be removed in the future developmet.
				productIdList: productInfo?.[0]?.isSet ? productInfo?.[0]?.setMemberProductIds : productIds,
				threeDFor: selectedSampleType?.id,
				requiredDate,
				taskValueType: 'REQUEST_3D'
			};
			productDevelopmentAdd({ body })
				.unwrap()
				.then(() => {
					handleSuccess();
				})
				.catch((error) => {
					toastError(error?.data?.message || 'Something went wrong');
				});
		} else if (requestType === SAMPLE_REQUEST_TYPES.SEND_TO_GRAPHIC) {
			const body = {
				productActionId: 389, // Will be removed in the future developmet.
				productIdList: productInfo?.[0]?.isSet ? productInfo?.[0]?.setMemberProductIds : productIds,
				assignedToId: selectedUser?.id,
				requiredDate,
				taskValueType: 'REQUEST_GRAPHICS'
			};
			productDevelopmentAdd({ body })
				.unwrap()
				.then(() => {
					handleSuccess();
				})
				.catch((error) => {
					toastError(error?.data?.message || 'Something went wrong');
				});
		}
	};

	return (
		<LoaderComponent loading={isSubmitting || productLoading}>
			<ModalContainer className="update-task-modal">
				<div className="popup-wrapper">
					<div className="common-popup update-task-modal">
						<ModalHeader onClose={onClose} title={title} />
						<div className="common-popup-body scroll-y-label">
							<div className="row">
								<div className="col-md-12">
									<InputField
										labelTitle="Required date*"
										type="date"
										value={requiredDate}
										onChangeHandler={(e) => {
											setRequiredDate(e.target.value);
											setErrors((prev) => ({
												...prev,
												requiredDate: ''
											}));
										}}
										required
										name="requiredDate"
										errors={errors?.requiredDate}
									/>
								</div>
							</div>
							{requestType === SAMPLE_REQUEST_TYPES.SEND_TO_GRAPHIC && (
								<div className="row">
									<div className="col-md-12 mb-4">
										<label htmlFor="collection">Graphics designer*</label>
										<CustomDropdown
											title="Select Graphics designer"
											onItemClick={(value) => {
												setSelectedUser(value);
												setErrors((prev) => ({
													...prev,
													selectedUser: ''
												}));
											}}
											selectedItem={selectedUser?.value}
											items={[
												...(userList?.FASHION_DESIGNER || []),
												...(userList?.GRAPHICS_DESIGNER || []),
												...(userList?.THREE_D_DESIGNER || [])
											]?.map((user) => ({
												...user,
												value: user?.name
											}))}
										/>
										{errors?.selectedUser && (
											<p className="error">{errors?.selectedUser}</p>
										)}
									</div>
								</div>
							)}
							{requestType === SAMPLE_REQUEST_TYPES.SEND_TO_3D && (
								<div className="row mb-4">
									<div className="col-md-12">
										<label htmlFor="collection">Sample type*</label>
										<CustomDropdown
											title="Select 3D for"
											onItemClick={(value) => {
												setSelectedSampleType(value);
												setErrors((prev) => ({
													...prev,
													selectedSampleType: ''
												}));
											}}
											selectedItem={selectedSampleType?.value}
											items={formatSampleTypeOptions(sampleTypeList)}
										/>
										{errors?.selectedSampleType && (
											<p className="error">{errors?.selectedSampleType}</p>
										)}
									</div>
								</div>
							)}
							<div className="send-pd-table">
								<table className="table with-border">
									<thead>
										<tr>
											<th class="design-details-column">
												<span class="ml-2">Design({productInfo?.length})</span>
											</th>
										</tr>
									</thead>
									<tbody>
										{productInfo?.map((item) => (
											<tr key={item?.id}>
												<td>
													<div className="d-flex align-items-center">
														{item?.isSet ? (
															<div
																className="set-image-layout"
																style={{ width: ' 56px' }}
															>
																<CommonImage
																	src={item?.designDocuments[0]?.docUrl}
																	alt="image"
																	className="cell-img pr-2"
																	isStatic={false}
																/>
																<CommonImage
																	src={item?.designDocuments[1]?.docUrl}
																	alt="image"
																	className="cell-img pr-2"
																	isStatic={false}
																/>
															</div>
														) : (
															<CommonImage
																src={item?.designDocuments[0]?.docUrl}
																alt="image"
																className="cell-img pr-2"
																isStatic={false}
															/>
														)}
														<div className="ml-12">
															{item?.isSet && setTag()}
															{item?.isPack && packTag()}
															<p className="regular-12 gray_dark_01 py-1">
																{item?.name}
															</p>
															{item?.isSet ? (
																item?.setMemberProducts?.map((member) => (
																	<ul className="common-list-component mb-1 mt-2">
																		<li>{member?.productGroup}</li>
																		<li>{member?.category}</li>
																	</ul>
																))
															) : (
																<ul className="common-list-component mb-2">
																	<li>{item?.productGroup}</li>
																	<li>{item?.category}</li>
																</ul>
															)}
														</div>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						</div>
						<ModalFooter onSubmit={handleSubmit} />
					</div>
				</div>
			</ModalContainer>
		</LoaderComponent>
	);
};

export default RequestTo3DAndGraphicsModal;
